// Which chord-only corpus rows have a written melody version (#419).
//
// The About panel's relations come from the Jazz 1460, which is chord-only, and
// every row opens as a `?tune=` link. That link resolves the way useTuneUrl
// does — a saved tune first, then a bundled score, then openbook — and falls
// back to the iReal chart. So a row "has a melody" exactly when that resolution
// lands on a score with a written line. A realbook tune is only reached when
// the link names the source, so a realbook-only match carries it.

export interface MelodyIndexInputs {
  saved: readonly { title: string; composer?: string; melody: boolean }[];
  bundled: readonly { title: string; composer?: string; hasMelody: boolean }[];
  openbook: readonly { title: string; composer?: string }[];
  realbook: readonly { title: string; composer?: string }[];
}

/** How to reach the melody version: a plain link, or one naming realbook. */
export interface MelodyVersion {
  source?: 'realbook';
}

export type MelodyLookup = (title: string, composer?: string) => MelodyVersion | null;

const key = (text: string | undefined) => (text ?? '').trim().toLowerCase();

function pick<T extends { title: string; composer?: string }>(
  rows: readonly T[],
  title: string,
  composer: string,
): T | undefined {
  const matches = rows.filter((row) => key(row.title) === title);
  return matches.find((row) => key(row.composer) === composer) ?? matches[0];
}

export function buildMelodyLookup(inputs: MelodyIndexInputs): MelodyLookup {
  return (rawTitle, rawComposer) => {
    const title = key(rawTitle);
    const composer = key(rawComposer);
    if (!title) return null;
    // The link opens the first source that holds the title, melody or not.
    const saved = pick(inputs.saved, title, composer);
    if (saved) return saved.melody ? {} : null;
    const bundled = pick(inputs.bundled, title, composer);
    if (bundled) return bundled.hasMelody ? {} : null;
    if (pick(inputs.openbook, title, composer)) return {};
    if (pick(inputs.realbook, title, composer)) return { source: 'realbook' };
    return null;
  };
}
